import { useCallback, useMemo, useState } from 'react';

import { AnnouncementDashboardNotification } from '@web/announcements/types/AnnouncementDashboardNotification';
import { mapAnnouncement } from '@web/announcements/utils/mappers';
import { getNotificationActionByType } from '@web/dashboard/utils/getNotificationActionByType';
import { DashboardNotificationActionType } from '@web/dashboard/types';
import { useDashboardNotificationAction } from '@web/dashboard/hooks/useDashboardNotificationAction';

type Props = {
  notification: AnnouncementDashboardNotification;
  onClose: () => void;
};

export const useAnnouncementNotification = ({ notification, onClose }: Props) => {
  const [notificationIndex, setNotificationIndex] = useState(0);
  const [isDismissing, setIsDismissing] = useState(false);
  const { invoke } = useDashboardNotificationAction();
  const { actions, items, id: notificationId } = notification;

  const announcements = useMemo(
    () =>
      items
        .map((item) => mapAnnouncement({ ...item, recipientCount: 0, readCount: 0 }))
        .sort((a, b) => a.id - b.id),
    [items]
  );

  const announcement = announcements[notificationIndex];
  const hasPrev = notificationIndex > 0;
  const hasNext = notificationIndex < items.length - 1;

  const nextAnnouncement = useCallback(() => {
    if (hasNext) {
      setNotificationIndex(notificationIndex + 1);
    }
  }, [hasNext, notificationIndex]);

  const prevAnnouncement = useCallback(() => {
    if (hasPrev) {
      setNotificationIndex(notificationIndex - 1);
    }
  }, [hasPrev, notificationIndex]);

  const dismissNotification = useCallback(async () => {
    const dismissAction = getNotificationActionByType(
      actions,
      DashboardNotificationActionType.dismiss
    );

    if (dismissAction) {
      setIsDismissing(true);
      await invoke({ id: notificationId, url: dismissAction.url });
      setIsDismissing(false);
    }

    onClose();
  }, [actions, invoke, notificationId, onClose]);

  return {
    announcement,
    dismissNotification,
    hasNext,
    hasPrev,
    isDismissing,
    itemsCount: announcements.length,
    nextAnnouncement,
    notificationIndex,
    prevAnnouncement,
  };
};
